import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PropTypes from "prop-types";
import axios from "axios";
import AdmittedBatchSelect from "./AdmittedBatchSelect";
import ShowExamDatesDialog from "./ShowExamDatesDialog";
import ValuationScheduleDialog from "./ValuationScheduleDialog";
export default function ActionsMenu({
  row,
  user,
  onEdit,
  onDelete,
  onCreate,
  showSnackbar,
  onGenerateTimetable,
  onGenerateSeatingPlan,
}) {
  const isPublished = row?.workflow_status === "PUBLISHED";
  const examScheduleMasterId = row.exam_schedule_master_id;

  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false); // Create/Edit dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false); // View schedule dialog
  const [yearSemesters, setYearSemesters] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [admittedBatches, setAdmittedBatches] = useState([]);
  const [slots, setSlots] = useState([]);
  const [errors, setErrors] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [openValuationDialog, setOpenValuationDialog] = useState(false);
  const [form, setForm] = useState({
    year_number: "",
    semester_number: "",
    exam_theory_slot_id: "",
    exam_practical_slot_id: "",
    exam_fee: "",
    admitted_batches: [],
  });
  const [editingDetail, setEditingDetail] = useState(null);
  const [scheduleDetails, setScheduleDetails] = useState([]);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const [openDatesDialog, setDatesOpenDialog] = useState(false);

  useEffect(() => {
    if (user?.college_id && user?.client_id) {
      if (row.program_id !== null) {
        axios
          .get(
            `${API_BASE_URL}/api/program-year-semesters/${user.client_id}/${user.college_id}/${row.program_id}`,
            { headers: { "Cache-Control": "no-cache" } }
          )
          .then((res) => setYearSemesters(res.data.data.items || []))
          .catch((err) => console.error("Error fetching year/semesters", err));
      } else {
        axios
          .get(`${API_BASE_URL}/api/program-year-semesters/${user.client_id}/${user.college_id}`, {
            headers: { "Cache-Control": "no-cache" },
          })
          .then((res) => setYearSemesters(res.data.data.items || []))
          .catch((err) => console.error("Error fetching year/semesters", err));
      }
    }
  }, [user?.college_id, user?.client_id]);

  // Fetch admitted batches
  useEffect(() => {
    if (user?.college_id && user?.client_id) {
      axios
        .get(`${API_BASE_URL}/api/admittedbatches/${user.client_id}/${user.college_id}`, {
          headers: { "Cache-Control": "no-cache" },
        })
        .then((res) => setAdmittedBatches(res?.data?.items ?? []))
        .catch((err) => console.error("Error fetching admitted batches", err));
    }
  }, [user?.college_id, user?.client_id]);

  // Fetch exam slots
  useEffect(() => {
    if (user?.college_id && user?.client_id) {
      axios
        .get(`${API_BASE_URL}/api/exam-slots/${user.client_id}/${user.college_id}`)
        .then((res) => setSlots(res.data.data.items || []))
        .catch((err) => console.error("Error fetching slots", err));
    }
  }, [user?.college_id, user?.client_id]);

  // Filter semesters based on year
  useEffect(() => {
    if (form.year_number) {
      const yearNum = Number(form.year_number);
      const sems = yearSemesters.filter((ys) => ys.year_number === yearNum);
      setSemesters(sems);

      // ✅ Only reset semester_number if it's not part of the new year's semesters
      if (!sems.some((s) => s.semester_number === Number(form.semester_number))) {
        setForm((prev) => ({ ...prev, semester_number: "" }));
      }
    } else {
      setSemesters([]);
    }
  }, [form.year_number, yearSemesters]);

  // Open create dialog
  const handleDialogOpen = () => {
    setEditingDetail(null);
    setForm({
      year_number: "",
      semester_number: "",
      exam_theory_slot_id: "",
      exam_practical_slot_id: "",
      exam_fee: "",
      admitted_batches: [],
    });
    handleMenuClose();
    setOpenDialog(true);
  };

  // Open edit dialog
  const handleEditDetail = (detail) => {
    setEditingDetail(detail);

    setForm({
      year_number: detail.year_number,
      semester_number: detail.semester_number,
      exam_theory_slot_id: detail.exam_theory_slot_id,
      exam_practical_slot_id: detail.exam_practical_slot_id,
      exam_fee: detail.exam_fee,
      admitted_batches: detail.schedule_batch_maps?.map((b) => b.admittedbatch_id.toString()) || [],
    });
    handleMenuClose();
    setOpenDialog(true);
  };

  // Delete schedule detail
  const handleDeleteDetail = async (detail) => {
    if (!window.confirm("Delete this schedule detail?")) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/exam-external-schedule-details/${user.client_id}/${user.college_id}/${detail.exam_schedule_detail_id}`
      );
      setScheduleDetails((prev) =>
        prev.filter((d) => d.exam_schedule_detail_id !== detail.exam_schedule_detail_id)
      );
      showSnackbar("Schedule detail deleted", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to delete schedule detail", "error");
    }
  };

  const handleSave = async () => {
    if (!form.admitted_batches || form.admitted_batches.length === 0) {
      showSnackbar("Please select at least one batch", "warning");
      return;
    }
    const payload = {
      exam_schedule_master_id: row.exam_schedule_master_id,
      start_enroll_date: row.start_enroll_date,
      end_enroll_date: row.end_enroll_date,
      exam_type_id: row.exam_type_id,
      exam_month: row.exam_month,
      year_number: form.year_number,
      semester_number: form.semester_number,
      exam_theory_slot_id: form.exam_theory_slot_id,
      exam_practical_slot_id: form.exam_practical_slot_id,
      exam_fee: form.exam_fee,
      admitted_batches: form.admitted_batches,
      college_id: user.college_id,
      program_id: user.program_id,
      created_by: user.user_email,
    };

    try {
      let response;

      if (editingDetail) {
        response = await axios.put(
          `${API_BASE_URL}/api/exam-external-schedule-details/${user.client_id}/${user.college_id}/${editingDetail.exam_schedule_detail_id}`,
          payload
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/exam-external-schedule-details/${user.client_id}/${user.college_id}`,
          payload
        );
      }

      // ✅ Check API response success flag
      if (response.data?.success === false) {
        showSnackbar(response.data.message || "Operation failed", "warning");
        return; // stop execution
      }

      // ✅ Continue only if success = true
      showSnackbar(
        editingDetail ? "Exam schedule updated successfully" : "Exam schedule created successfully",
        "success"
      );

      // 🔑 Refresh list
      const { data } = await axios.get(
        `${API_BASE_URL}/api/exam-external-schedule-details/${user.client_id}/${user.college_id}/master/${row.exam_schedule_master_id}`,
        { headers: { "Cache-Control": "no-cache" } }
      );

      setScheduleDetails((data?.data?.items || []).filter(Boolean));
      setOpenDialog(false);
      if (onCreate) onCreate(); // refresh parent data
    } catch (err) {
      console.error(err);
      // ✅ Handle network or server errors (non-200)
      const message = err.response?.data?.message || "Failed to save schedule. Please try again.";
      showSnackbar(message, "error");
    }
  };

  const handleViewDialogOpen = async () => {
    handleMenuClose();
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/exam-external-schedule-details/${user.client_id}/${user.college_id}/master/${row.exam_schedule_master_id}`,
        { headers: { "Cache-Control": "no-cache" } }
      );
      setScheduleDetails(data.data.items || []);
      setViewDialogOpen(true);
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load schedule details", "error");
    }
  };

  const handleViewDialogClose = () => setViewDialogOpen(false);
  const handleDialogClose = () => setOpenDialog(false);

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 70,
        sortable: false,
        valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      { field: "year_number", headerName: "Study Year", flex: 0.5, sortable: false },
      { field: "semester_number", headerName: "Semester", flex: 0.5, sortable: false },
      {
        field: "exam_theory_slot_id",
        headerName: "Theory Slot",
        flex: 1,
        valueGetter: (params) =>
          slots.find((s) => s.exam_slot_id === params.row.exam_theory_slot_id)?.slot_name || "",
      },
      {
        field: "exam_practical_slot_id",
        headerName: "Practical Slot",
        flex: 1,
        type: "string",
        valueGetter: (params) =>
          slots.find((s) => s.exam_slot_id === params.row.exam_practical_slot_id)?.slot_name || "",
      },
      { field: "exam_fee", headerName: "Exam Fee", flex: 0.5 },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <MenuPopupCell row={params.row} onEdit={handleEditDetail} onDelete={handleDeleteDetail} />
        ),
      },
    ],
    [slots]
  );

  return (
    <>
      {/* Main Actions Button */}
      <Button
        variant="contained"
        size="small"
        startIcon={<MoreVertIcon />}
        sx={{
          backgroundColor: "#1976d2",
          color: "#fff",
          textTransform: "none",
          "&:hover": { backgroundColor: "#115293" },
        }}
        onClick={handleMenuClick}
      >
        Actions
      </Button>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose}>
        <MenuItem
          disabled={isPublished}
          onClick={() => {
            handleMenuClose();
            onEdit(row);
          }}
          sx={{ color: "green", "&:hover": { backgroundColor: "rgba(0,0,255,0.1)" } }}
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} />
          Edit Exam Schedule
        </MenuItem>

        <MenuItem
          disabled={isPublished}
          onClick={() => {
            handleMenuClose();
            onDelete(row);
          }}
          sx={{ color: "red", "&:hover": { backgroundColor: "rgba(255,0,0,0.1)" } }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete Exam Schedule
        </MenuItem>

        <MenuItem
          disabled={isPublished}
          onClick={handleDialogOpen}
          sx={{ color: "green", "&:hover": { backgroundColor: "rgba(0,255,0,0.1)" } }}
        >
          <AddIcon fontSize="small" style={{ marginRight: 8 }} />
          Create Semester Schedule
        </MenuItem>

        <MenuItem
          disabled={isPublished}
          onClick={handleViewDialogOpen}
          sx={{ color: "green", "&:hover": { backgroundColor: "rgba(255,165,0,0.1)" } }}
        >
          <MoreVertIcon fontSize="small" style={{ marginRight: 8 }} />
          View Semester Schedules
        </MenuItem>

        <MenuItem
          disabled={isPublished}
          onClick={() => {
            handleMenuClose();
            setOpenValuationDialog(true);
          }}
          sx={{ color: "green", "&:hover": { backgroundColor: "rgba(103,58,183,0.1)" } }}
        >
          <ScheduleIcon fontSize="small" style={{ marginRight: 8 }} />
          Valuation Schedules
        </MenuItem>
        <MenuItem
          disabled={isPublished}
          onClick={() => {
            handleMenuClose();
            onGenerateTimetable(row);
          }}
          sx={{
            color: "green",
            "&:hover": { backgroundColor: "rgba(0,128,0,0.1)" },
          }}
        >
          <ScheduleIcon fontSize="small" style={{ marginRight: 8 }} />
          Generate Timetable
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMenuClose();
            setDatesOpenDialog(true);
          }}
          sx={{
            color: "green",
            "&:hover": { backgroundColor: "rgba(0,128,0,0.1)" },
          }}
        >
          <ScheduleIcon fontSize="small" style={{ marginRight: 8 }} />
          Show Exam Dates
        </MenuItem>
        {/* <MenuItem
          onClick={() => {
            handleMenuClose();
            onGenerateSeatingPlan(row);
          }}
          sx={{
            color: "green",
            "&:hover": { backgroundColor: "rgba(0,128,0,0.1)" },
          }}
        >
          <ScheduleIcon fontSize="small" style={{ marginRight: 8 }} />
          Generate Seating Plan
        </MenuItem> */}
      </Menu>
      <ShowExamDatesDialog
        open={openDatesDialog}
        onClose={() => setDatesOpenDialog(false)}
        examScheduleMasterId={examScheduleMasterId}
        showSnackbar={showSnackbar}
      />

      <ValuationScheduleDialog
        row={row}
        open={openValuationDialog}
        onClose={() => setOpenValuationDialog(false)}
        examScheduleMasterId={row.exam_schedule_master_id}
        examDate={row.exam_date}
        programId={row.program_id}
        branchId={row.branch_id}
        showSnackbar={showSnackbar}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} fullWidth>
        <DialogTitle>
          {editingDetail ? "Edit" : "Create"} Schedule for {row.exam_type_name} - {row.exam_month}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="normal"
            label="Study Year"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.year_number}
            onChange={(e) => setForm((prev) => ({ ...prev, year_number: e.target.value }))}
            SelectProps={{ native: true }}
          >
            <option value="">Select Year</option>
            {Array.from(new Set(yearSemesters.map((ys) => ys.year_number))).map((year, idx) => (
              <option key={idx} value={year}>
                {year}
              </option>
            ))}
          </TextField>

          <TextField
            select
            margin="normal"
            label="Semester"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.semester_number}
            onChange={(e) => setForm((prev) => ({ ...prev, semester_number: e.target.value }))}
            SelectProps={{ native: true }}
          >
            <option value="">Select Semester</option>
            {semesters.map((sem, idx) => (
              <option key={idx} value={sem.semester_number}>
                {sem.semester_number}
              </option>
            ))}
          </TextField>

          <TextField
            select
            margin="normal"
            label="Exam Theory Time"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.exam_theory_slot_id}
            onChange={(e) => setForm((prev) => ({ ...prev, exam_theory_slot_id: e.target.value }))}
            SelectProps={{ native: true }}
          >
            <option value="">Select Theory Time</option>
            {slots.map((s) => (
              <option key={s.exam_slot_id} value={s.exam_slot_id}>
                {s.description}
              </option>
            ))}
          </TextField>

          <TextField
            select
            margin="normal"
            label="Exam Practical Time"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.exam_practical_slot_id}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, exam_practical_slot_id: e.target.value }))
            }
            SelectProps={{ native: true }}
          >
            <option value="">Select Practical Slot</option>
            {slots.map((s) => (
              <option key={s.exam_slot_id} value={s.exam_slot_id}>
                {s.description}
              </option>
            ))}
          </TextField>

          <TextField
            margin="normal"
            label="Exam Fee"
            type="number"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.exam_fee}
            onChange={(e) => setForm((prev) => ({ ...prev, exam_fee: e.target.value }))}
          />

          <AdmittedBatchSelect
            admittedBatches={admittedBatches}
            form={form}
            setForm={setForm}
            errors={errors}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{
              backgroundColor: "#ff9800",
              color: "#fff",
              "&:hover": { backgroundColor: "#c77700" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Schedule Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleViewDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          View Schedule Details for {row.exam_type_name} -{row.exam_month}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={scheduleDetails}
              columns={columns}
              getRowId={(row) => row.exam_schedule_detail_id}
              pageSize={pageSize} // controlled pageSize
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} // update state
              rowsPerPageOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              pagination
              disableSelectionOnClick
              autoHeight
              disableColumnMenu // optional: disables column menu (3 dots)
              onCellClick={(params, event) => event.stopPropagation()} // block cell clicks
              onRowClick={(params, event) => event.stopPropagation()} // block row clicks
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Popup menu inside DataGrid cell
function MenuPopupCell({ row, onEdit, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            onEdit(row);
            setAnchorEl(null);
          }}
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDelete(row);
            setAnchorEl(null);
          }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Delete
        </MenuItem>
      </Menu>
    </>
  );
}

MenuPopupCell.propTypes = {
  row: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

ActionsMenu.propTypes = {
  row: PropTypes.object.isRequired,
  user: PropTypes.shape({
    college_id: PropTypes.number.isRequired,
    program_id: PropTypes.number.isRequired,
    client_id: PropTypes.number.isRequired,
    user_email: PropTypes.string.isRequired,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  showSnackbar: PropTypes.func.isRequired,
  onGenerateTimetable: PropTypes.func.isRequired,
  onGenerateSeatingPlan: PropTypes.func.isRequired,
};
